import * as Clipboard from "expo-clipboard";

import { AHText } from "arkh";
import React, { useEffect, useMemo } from "react";

import { useInterval } from "widgets/useInterval";
import { FormattedMessage } from "react-intl";
import { useAlert } from "../../common/Alert";
import { StoreAdminNavigationProp } from "../StoreAdmin/StoreAdminNavigation";
import { graphql, useMutation } from "react-relay";
import { IntegrationActivateScreen } from "./components/IntegrationActivation";
import { IntegrationFlyerCreateScreenFlyerMutation } from "./__generated__/IntegrationFlyerCreateScreenFlyerMutation.graphql";
import { IntegrationFlyerCreateScreenPollFileMutation } from "./__generated__/IntegrationFlyerCreateScreenPollFileMutation.graphql";
import { Platform } from "react-native";

function parseFlyerUrl(url: string) {
  if (url.includes("e-magin.se/latestpaper/")) {
    const emaginSplit = url.split("e-magin.se/latestpaper/");
    if (emaginSplit.length > 1) {
      const emaginSplit2 = emaginSplit[1].split("/");
      if (emaginSplit2.length > 1) {
        return {
          provider: "EMAGIN" as const,
          id: emaginSplit2[0],
        };
      }
    }
  } else if (url.includes("ereklamblad.se")) {
    let id = url.match(/~s:([^/]+)/)?.[1];
    id ??= url.match(/\/butiker\/([^/]+)/)?.[1];
    if (id && id.length > 3 && id.length < 30) {
      return {
        provider: "EREKLAMBLAD" as const,
        id,
      };
    }
  } else if (url.includes("ekostormarknad.se/")) {
    const storeName = url.split("ekostormarknad.se/")[1].split("/")[0];
    if (storeName.length !== 0) {
      return {
        provider: "EKOSTORMARKNAD" as const,
        id: storeName,
      };
    }
  } else if (url.includes("dr.coop.se")) {
    return {
      provider: "POLL_FILE" as const,
      url,
    };
  }
  return null;
}

export const IntegrationFlyerCreateScreen = (props: {
  navigation: StoreAdminNavigationProp<"IntegrationFlyerCreate">;
}) => {
  const [value, setValue] = React.useState("");

  const parsedValue = useMemo(() => parseFlyerUrl(value), [value]);

  useEffect(() => {
    let remove: undefined | (() => void);
    if (Platform.OS !== "web") {
      remove = Clipboard.addClipboardListener(async (e) => {
        setValue(
          await Clipboard.getStringAsync({
            preferredFormat: Clipboard.StringFormat.PLAIN_TEXT,
          })
        );
      }).remove;
    }
    let mounted = true;
    Clipboard.getStringAsync({
      preferredFormat: Clipboard.StringFormat.PLAIN_TEXT,
    }).then((v) => {
      if (mounted) {
        setValue(v);
      }
    });

    return () => {
      mounted = false;
      remove?.();
    };
  }, []);

  const [createFlyer, createFlyerLoading] =
    useMutation<IntegrationFlyerCreateScreenFlyerMutation>(graphql`
      mutation IntegrationFlyerCreateScreenFlyerMutation(
        $id: String!
        $provider: IntegrationFlyerProvider!
      ) {
        store {
          createIntegrationFlyer(id: $id, provider: $provider) {
            id
          }
        }
      }
    `);

  const [createPollFile, createPollFileLoading] =
    useMutation<IntegrationFlyerCreateScreenPollFileMutation>(graphql`
      mutation IntegrationFlyerCreateScreenPollFileMutation($url: String!) {
        store {
          createIntegrationPollFile(url: $url) {
            id
          }
        }
      }
    `);

  const loading = createFlyerLoading || createPollFileLoading;

  const Alert = useAlert();
  const create = () => {
    if (!parsedValue) {
      throw new Error("Invalid url");
    }
    const onError = () => {
      Alert.alert(
        "Fel Uppstod",
        "Kunde inte skapa integrationen, kontrollera att du har knappat in rätt länk "
      );
    };
    if (parsedValue.provider === "POLL_FILE") {
      createPollFile({
        onError,
        onCompleted: (d) => {
          if (d.store.createIntegrationPollFile?.id) {
            props.navigation.goBack();
          } else {
            onError();
          }
        },
        variables: { url: parsedValue.url },
      });
    } else {
      createFlyer({
        onError,
        onCompleted: (d) => {
          if (d.store.createIntegrationFlyer?.id) {
            props.navigation.goBack();
          } else {
            onError();
          }
        },
        variables: { id: parsedValue.id, provider: parsedValue.provider },
      });
    }
  };

  const [placeHolderMode, setPlaceHolderMode] = React.useState(false);
  useInterval(() => setPlaceHolderMode((p) => !p), 1000);

  return (
    <IntegrationActivateScreen
      onActivate={create}
      isActivating={loading}
      isValid={!!parsedValue}
      header={
        <IntegrationActivateScreen.Header
          logo={
            <AHText type="title">
              <FormattedMessage
                description="IntegrationsAdminFlyer create screen title"
                defaultMessage="Online Leaflet"
              />
            </AHText>
          }
        />
      }
      activation={
        <IntegrationActivateScreen.Input
          value={value}
          onChangeText={setValue}
          type="url"
          label={
            <FormattedMessage
              description="IntegrationsAdminFlyer create screen instruction"
              defaultMessage="Paste a link to your online leaflet here"
            />
          }
          placeholder={
            placeHolderMode
              ? "https://ereklamblad.se/"
              : "https://www.e-magin.se/latestpaper/....."
          }
        />
      }
    />
  );
};
